import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, RefreshCw, Play, Send, Clock, Check, X,
  ShoppingCart, FileText, CreditCard, Calendar,
  Star, Gift, ChevronDown, ChevronUp, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateUtils';

import { getToken } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SCENARIO_CONFIG = {
  panier_abandonne:      { icon: ShoppingCart, color: '#F97316', label: '🛒 Panier abandonné' },
  devis_non_signe:       { icon: FileText,     color: '#F59E0B', label: '📋 Devis non signé' },
  acompte_manquant:      { icon: CreditCard,   color: '#EF4444', label: '💳 Acompte manquant' },
  evenement_proche_j14:  { icon: Calendar,     color: '#8B5CF6', label: '📅 J-14 avant événement' },
  evenement_proche_j3:   { icon: Zap,          color: '#EF4444', label: '⚡ J-3 avant événement' },
  solde_manquant:        { icon: CreditCard,   color: 'var(--admin-gold)', label: '💰 Solde à régler' },
  post_evenement:        { icon: Star,          color: '#22C55E', label: '⭐ Demande d\'avis' },
  fidelisation:          { icon: Gift,          color: 'var(--admin-teal)', label: '🎁 Fidélisation' },
};

export default function AdminRelancesAuto() {
  const [historique, setHistorique] = useState([]);
  const [statsParScenario, setStatsParScenario] = useState({});
  const [scenarios, setScenarios] = useState({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedLog, setExpandedLog] = useState(null);
  const [filtreScenario, setFiltreScenario] = useState('all');
  const [manualForm, setManualForm] = useState({ scenario: 'devis_non_signe', email: '', nom: '', quote_id: '' });
  const [sendingManual, setSendingManual] = useState(false);

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/relances-auto/historique?limit=100`, { headers });
      const data = await res.json();
      setHistorique(data.logs || []);
      setStatsParScenario(data.stats_par_scenario || {});
      setScenarios(data.scenarios || {});
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const runDryRun = async () => {
    setRunning(true);
    setDryRunResult(null);
    try {
      const res = await fetch(`${BACKEND}/api/admin/relances-auto/run?dry_run=true`, { method: 'POST', headers });
      const data = await res.json();
      setDryRunResult(data);
      toast.success(`Simulation : ${data.simulees} relance(s) à envoyer`);
    } catch { toast.error('Erreur simulation'); }
    finally { setRunning(false); }
  };

  const runReal = async () => {
    if (!window.confirm('Envoyer toutes les relances automatiques maintenant ?')) return;
    setRunning(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/relances-auto/run?dry_run=false`, { method: 'POST', headers });
      const data = await res.json();
      toast.success(`✅ ${data.envoyees} relance(s) envoyée(s) · ${data.erreurs} erreur(s)`);
      load();
    } catch { toast.error('Erreur envoi'); }
    finally { setRunning(false); }
  };

  const runPaniersAbandonnes = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/admin/relances-auto/paniers-abandonnes?dry_run=false`, { method: 'POST', headers });
      const data = await res.json();
      toast.success(`${data.sent} panier(s) abandonné(s) relancé(s)`);
      load();
    } catch { toast.error('Erreur'); }
  };

  const sendManual = async () => {
    if (!manualForm.email || !manualForm.scenario) { toast.error('Email et scénario requis'); return; }
    setSendingManual(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/relances-auto/manuel`, {
        method: 'POST', headers,
        body: JSON.stringify({
          scenario: manualForm.scenario,
          client_email: manualForm.email,
          client_nom: manualForm.nom || 'Client',
          quote_id: manualForm.quote_id,
          ctx: { nom: manualForm.nom, numero: manualForm.quote_id },
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Email envoyé à ${manualForm.email}`);
        setManualForm(prev => ({ ...prev, email: '', nom: '', quote_id: '' }));
        load();
      } else {
        toast.error('Erreur envoi email');
      }
    } catch { toast.error('Erreur'); }
    finally { setSendingManual(false); }
  };

  const filteredLogs = filtreScenario === 'all' ? historique : historique.filter(l => l.scenario === filtreScenario);
  const totalEnvoye = Object.values(statsParScenario).reduce((s, v) => s + v, 0);

  const TABS = [
    { id: 'dashboard', label: '🎯 Dashboard' },
    { id: 'scenarios', label: '⚙️ Scénarios' },
    { id: 'historique', label: `📨 Historique (${historique.length})` },
    { id: 'manuel', label: '✉️ Envoi manuel' },
  ];

  return (
    <div data-testid="admin-relances-auto">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paiements & Factures</p>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginTop: 4 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
              letterSpacing: '-.6px', color: 'var(--admin-text)' }}>Relances automatiques</h2>
            <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
              Paniers abandonnés · Devis non signés · Post-événement · Fidélisation
            </p>
          </div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            <button onClick={() => load()}
              style={{ width: 34, height: 34, borderRadius: 10, border: '1px solid var(--admin-border)',
                background: 'var(--admin-surface)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-muted)' }}>
              <RefreshCw size={14}/>
            </button>
            <button onClick={runDryRun} disabled={running}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                background: 'var(--admin-amber-dim)', color: 'var(--admin-amber)',
                border: '1px solid rgba(227,165,58,.3)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <Play size={13}/> Simuler
            </button>
            <button onClick={runReal} disabled={running}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                background: 'var(--admin-amber)', color: '#2a1d05', border: 'none',
                cursor: running ? 'wait' : 'pointer', fontSize: 13, fontWeight: 700, opacity: running ? .7 : 1 }}>
              <Send size={13}/> {running ? 'En cours...' : 'Lancer maintenant'}
            </button>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Total envoyées',       value: totalEnvoye,                           color: 'var(--admin-teal)',   icon: <Send size={14}/> },
          { label: 'Devis non signés',      value: statsParScenario.devis_non_signe || 0, color: 'var(--admin-amber)',  icon: <FileText size={14}/> },
          { label: 'Acomptes manquants',    value: statsParScenario.acompte_manquant || 0, color: 'var(--admin-red)',   icon: <CreditCard size={14}/> },
          { label: 'Post-événement',        value: statsParScenario.post_evenement || 0,  color: 'var(--admin-green)',  icon: <Star size={14}/> },
        ].map((s, i) => (
          <div key={i} className="adm-card" style={{ padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ color: s.color }}>{s.icon}</span>
              <p style={{ fontSize: 11.5, color: 'var(--admin-faint)', margin: 0 }}>{s.label}</p>
            </div>
            <p style={{ fontFamily: 'var(--admin-font-display)', fontSize: 22, fontWeight: 700,
              color: s.color, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Résultat simulation */}
      {dryRunResult && (
        <div className="adm-card" style={{ padding: '14px 16px', marginBottom: 16,
          background: 'var(--admin-amber-dim)', borderColor: 'rgba(227,165,58,.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--admin-amber)' }}>
              🔍 Simulation — {dryRunResult.simulees} relance(s) à envoyer
            </p>
            <button onClick={() => setDryRunResult(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-faint)' }}>
              <X size={14}/>
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {dryRunResult.details?.slice(0, 8).map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--admin-muted)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  background: SCENARIO_CONFIG[d.scenario]?.color || 'var(--admin-faint)' }} />
                <span style={{ color: SCENARIO_CONFIG[d.scenario]?.color || 'var(--admin-faint)', fontWeight: 600 }}>
                  {SCENARIO_CONFIG[d.scenario]?.label || d.scenario}
                </span>
                <span>— {d.client} {d.email ? `(${d.email})` : ''}</span>
              </div>
            ))}
          </div>
          {dryRunResult.simulees > 0 && (
            <button onClick={runReal} className="mt-3 flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold"
              style={{ background: '#F97316', color: 'var(--admin-text)', border: 'none', cursor: 'pointer' }}>
              <Send size={12} /> Envoyer les {dryRunResult.simulees} relances
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b mb-5 overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="px-4 py-3 text-sm font-semibold border-b-2 flex-shrink-0"
            style={{ borderColor: activeTab === tab.id ? '#F97316' : 'transparent', color: activeTab === tab.id ? '#F97316' : 'var(--text-muted)', background: 'none', cursor: 'pointer' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── DASHBOARD ─────────────────────────────────────────────────────── */}
      {activeTab === 'dashboard' && (
        <div className="space-y-3">
          <div className="p-4 rounded-2xl" style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.2)' }}>
            <p className="text-xs font-bold mb-2" style={{ color: 'var(--admin-teal)' }}>💡 Comment ça marche</p>
            <div className="grid sm:grid-cols-2 gap-2 text-xs" style={{ color: 'var(--admin-muted)' }}>
              <p>1. Cliquez <strong>"Simuler"</strong> pour voir ce qui sera envoyé sans envoyer</p>
              <p>2. Cliquez <strong>"Lancer maintenant"</strong> pour envoyer toutes les relances</p>
              <p>3. Les doublons sont évités automatiquement (pas de relance déjà envoyée)</p>
              <p>4. Utilisez <strong>"Envoi manuel"</strong> pour tester un template spécifique</p>
            </div>
          </div>

          {/* Paniers abandonnés */}
          <div className="adm-card p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShoppingCart size={18} style={{ color: '#F97316' }} />
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>Paniers abandonnés</p>
                  <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Envoyé après 2h d'inactivité</p>
                </div>
              </div>
              <button onClick={runPaniersAbandonnes}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold"
                style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)', cursor: 'pointer' }}>
                <Send size={12} /> Traiter maintenant
              </button>
            </div>
          </div>

          {/* Statuts par scénario */}
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.entries(SCENARIO_CONFIG).map(([key, conf]) => {
              const Icon = conf.icon;
              const count = statsParScenario[key] || 0;
              return (
                <div key={key} className="adm-card p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${conf.color}15` }}>
                    <Icon size={16} style={{ color: conf.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>{conf.label}</p>
                    <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{scenarios[key]?.desc || ''}</p>
                  </div>
                  <span className="font-bold text-lg" style={{ color: conf.color }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SCÉNARIOS ─────────────────────────────────────────────────────── */}
      {activeTab === 'scenarios' && (
        <div className="space-y-3">
          {Object.entries(scenarios).map(([key, sc]) => {
            const conf = SCENARIO_CONFIG[key] || {};
            const Icon = conf.icon || Zap;
            return (
              <div key={key} className="adm-card p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${conf.color}15` }}>
                    <Icon size={16} style={{ color: conf.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{sc.label}</p>
                      {sc.delai_h > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: `${conf.color}15`, color: conf.color }}>
                          <Clock size={10} className="inline mr-0.5" />{sc.delai_h}h
                        </span>
                      )}
                    </div>
                    <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{sc.desc}</p>
                  </div>
                  <span className="font-bold" style={{ color: conf.color }}>{statsParScenario[key] || 0} envoyées</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── HISTORIQUE ────────────────────────────────────────────────────── */}
      {activeTab === 'historique' && (
        <div>
          {/* Filtres */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => setFiltreScenario('all')}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold"
              style={{ background: filtreScenario === 'all' ? '#F97316' : 'var(--bg)', color: filtreScenario === 'all' ? '#FFF' : 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer' }}>
              Tous ({historique.length})
            </button>
            {Object.entries(SCENARIO_CONFIG).map(([k, conf]) => {
              const count = historique.filter(l => l.scenario === k).length;
              if (!count) return null;
              return (
                <button key={k} onClick={() => setFiltreScenario(k)}
                  className="px-3 py-1.5 rounded-xl text-xs font-semibold"
                  style={{ background: filtreScenario === k ? conf.color : `${conf.color}15`, color: filtreScenario === k ? '#FFF' : conf.color, border: `1px solid ${conf.color}40`, cursor: 'pointer' }}>
                  {conf.label.split(' ').slice(0, 2).join(' ')} ({count})
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 rounded-2xl animate-pulse" style={{ background: 'var(--border)' }} />)}</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-10">
              <Send size={32} style={{ color: 'var(--border)', margin: '0 auto 8px' }} />
              <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Aucune relance envoyée pour ce filtre</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredLogs.map(log => {
                const conf = SCENARIO_CONFIG[log.scenario] || {};
                return (
                  <div key={log.id} className="flex items-center gap-3 p-3 rounded-xl adm-card">
                    <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ background: conf.color || '#888' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: 'var(--admin-text)' }}>
                        {log.client_nom} — {log.client_email}
                      </p>
                      <p className="text-xs" style={{ color: conf.color || 'var(--text-muted)' }}>
                        {conf.label || log.scenario}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                        style={{ background: log.statut === 'envoyee' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: log.statut === 'envoyee' ? '#22C55E' : '#EF4444' }}>
                        {log.statut === 'envoyee' ? '✓ Envoyée' : '✗ Erreur'}
                      </span>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--admin-muted)' }}>
                        {formatDateTime(log.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── ENVOI MANUEL ──────────────────────────────────────────────────── */}
      {activeTab === 'manuel' && (
        <div className="max-w-lg">
          <div className="adm-card p-5 space-y-4">
            <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>Envoyer un email de test ou relance manuelle</p>

            <div>
              <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--admin-text)' }}>Scénario *</label>
              <select value={manualForm.scenario} onChange={e => setManualForm(p => ({ ...p, scenario: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm"
                style={{ border: '1px solid var(--border)', background: 'var(--admin-surface)', color: 'var(--admin-text)', outline: 'none' }}>
                {Object.entries(SCENARIO_CONFIG).map(([k, conf]) => (
                  <option key={k} value={k}>{conf.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--admin-text)' }}>Email destinataire *</label>
              <input type="email" value={manualForm.email} onChange={e => setManualForm(p => ({ ...p, email: e.target.value }))}
                placeholder="client@email.fr" className="w-full px-3 py-2.5 rounded-xl text-sm"
                style={{ border: '1px solid var(--border)', background: 'var(--admin-surface)', color: 'var(--admin-text)', outline: 'none' }} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--admin-text)' }}>Nom</label>
                <input type="text" value={manualForm.nom} onChange={e => setManualForm(p => ({ ...p, nom: e.target.value }))}
                  placeholder="Marie Dupont" className="w-full px-3 py-2.5 rounded-xl text-sm"
                  style={{ border: '1px solid var(--border)', background: 'var(--admin-surface)', color: 'var(--admin-text)', outline: 'none' }} />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color: 'var(--admin-text)' }}>Réf. devis (optionnel)</label>
                <input type="text" value={manualForm.quote_id} onChange={e => setManualForm(p => ({ ...p, quote_id: e.target.value }))}
                  placeholder="DEV-2026-XXXXX" className="w-full px-3 py-2.5 rounded-xl text-sm"
                  style={{ border: '1px solid var(--border)', background: 'var(--admin-surface)', color: 'var(--admin-text)', outline: 'none' }} />
              </div>
            </div>

            <button onClick={sendManual} disabled={sendingManual || !manualForm.email}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm"
              style={{ background: '#F97316', color: 'var(--admin-text)', border: 'none', cursor: 'pointer', opacity: sendingManual ? 0.7 : 1 }}>
              <Send size={15} /> {sendingManual ? 'Envoi...' : 'Envoyer l\'email'}
            </button>

            <div className="p-3 rounded-xl text-xs" style={{ background: 'rgba(93,188,191,0.06)', color: 'var(--admin-teal)' }}>
              💡 Utilisez votre propre email pour tester les templates avant l'envoi en production.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
